import React, { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Colors } from '_styles';
import { MdConnectingAirports, MdOutlineAccessTime } from "react-icons/md";
import { getTimeString } from "_utils/functions";
import { selectFlightById } from '_redux/slices/timeline/timeline-flight-list';
import { selectFirebaseInfo } from "_redux/slices/main-screen/user";
import { getFlightById } from '_services/flight-service';

const ConnectionFlightInformation = ({ onwards }) => {

    const [apiFlight, setApiFlight] = useState(null);
    const [apiStatus, setApiStatus] = useState('idle');
    const dbUser = useSelector(selectFirebaseInfo);
    const iata = dbUser?.details?.authority || '';

    const flightKey = useMemo(() => {
        if (!onwards?.carrierCode || !onwards?.flightNumber || !onwards?.origin || !onwards?.destination || !onwards?.departureDate) {
            return null;
        }
        return `${onwards.carrierCode}${onwards.flightNumber}_${onwards.origin}_${onwards.destination}_${onwards.departureDate}`;
    }, [onwards]);

    const flightSelector = useMemo(() => {
        if (!flightKey) return () => null;
        return selectFlightById(flightKey);
    }, [flightKey]);

    const localFlight = useSelector(flightSelector);

    useEffect(() => {
        if (localFlight || !onwards?.carrierCode || !onwards?.flightNumber || !onwards?.origin || !onwards?.departureDate || !iata) return;

        let cancelled = false;
        setApiStatus('loading');

        const dest = onwards?.destination || '';
        const flightDocId = `${onwards.carrierCode}${onwards.flightNumber}_${onwards.origin}_${dest ? dest + '_' : ''}${onwards.departureDate}_IATA_${iata}`;

        getFlightById(flightDocId)
            .then((data) => {
                if (cancelled) return;
                const match = Array.isArray(data) ? data[0] : data;
                setApiFlight(match ?? null);
                setApiStatus('succeeded');
            })
            .catch(() => {
                if (cancelled) return;
                setApiStatus('failed');
            });

        return () => { cancelled = true; };
    }, [localFlight, onwards?.carrierCode, onwards?.flightNumber, onwards?.origin, onwards?.destination, onwards?.departureDate, iata]);

    if (!onwards) return null;

    const flightData = localFlight || apiFlight;
    const isLoading = !localFlight && apiStatus === 'loading';

    const flightIdentity = onwards.carrierCode && onwards.flightNumber
        ? `${onwards.carrierCode}${onwards.flightNumber}`
        : null;

    const route = onwards.origin && onwards.destination
        ? `${onwards.origin} → ${onwards.destination}`
        : onwards.origin || onwards.destination || null;

    if (!flightIdentity && !route) return null;

    if (isLoading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                {flightIdentity && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <MdConnectingAirports size={14} color={Colors.PRM_LIGHT_BLUE} />
                        <span style={{ fontSize: 12, fontWeight: 'bold', color: '#333' }}>{flightIdentity}</span>
                    </div>
                )}
                {route && (
                    <div style={{ fontSize: 11, color: '#555', paddingLeft: flightIdentity ? 22 : 0 }}>{route}</div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, paddingLeft: flightIdentity ? 22 : 0 }}>
                    <MdOutlineAccessTime size={12} color={Colors.PRM_LIGHT_BLUE} />
                    <span style={{ fontSize: 10, color: '#999' }}>Loading...</span>
                </div>
            </div>
        );
    }

    const gate = flightData?.gate || flightData?.flightInformation?.gate;
    const stand = flightData?.stand || flightData?.flightInformation?.stand;
    const operationInfo = flightData?.operationInfo;
    const scheduledTime = flightData?.operationInfo?.time || flightData?.flightFeedTime || flightData?.originalFlightTime;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            {flightIdentity && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <MdConnectingAirports size={14} color={Colors.PRM_LIGHT_BLUE} />
                    <span style={{ fontSize: 12, fontWeight: 'bold', color: '#333' }}>
                        {flightIdentity}
                    </span>
                </div>
            )}
            {route && (
                <div style={{ fontSize: 11, color: '#555', paddingLeft: flightIdentity ? 22 : 0 }}>
                    {route}
                </div>
            )}
            {(gate || stand) && (
                <div style={{ fontSize: 11, color: '#555', paddingLeft: flightIdentity ? 22 : 0 }}>
                    {gate && `G:${gate}`}
                    {gate && stand && ' / '}
                    {stand && `S:${stand}`}
                </div>
            )}
            {operationInfo?.status && operationInfo?.time ? (
                <div style={{ fontSize: 11, color: '#555', paddingLeft: flightIdentity ? 22 : 0 }}>
                    {getTimeString(operationInfo.time)} ({operationInfo.status})
                </div>
            ) : scheduledTime ? (
                <div style={{ fontSize: 11, color: '#555', paddingLeft: flightIdentity ? 22 : 0 }}>
                    {getTimeString(scheduledTime)}
                </div>
            ) : null}
        </div>
    );
};

export default React.memo(ConnectionFlightInformation);
